<template>
  <div class="nearest-map-box">
    <div id="introInput" ref="introInput" class="introInput">
      <q-input
        ref="autocomplete"
        v-model="searchContent"
        outlined
        :placeholder="$t('googlemap.search')"
      >
        <template #prepend>
          <q-icon name="search" />
        </template>
      </q-input>
      <div v-show="showSearchResult && String(searchContent)" class="search-result-panel">
        <div v-show="autoCompleteList.length > 0">
          <div
            v-for="item in autoCompleteList"
            :key="item.place_id"
            class="result-item"
            @click="onClickAddressItem(item.place_id)"
          >
            <p class="result-item-desc" v-html="highLightStr(searchContent,item.description)" />
          </div>
        </div>
        <div v-show="autoCompleteList.length === 0" class="empty">No Matched</div>
      </div>
    </div>
    <div id="map" ref="map" class="full-width full-height" />
    <div id="infowindow-content" ref="infowindow_content">
      <span id="place-name" class="title" />
    </div>
  </div>
</template>

<script>
import { debounce } from 'quasar';
import { loadGoogleMap } from 'components/utils';
import { pushLog } from 'src/utils/pushLog';

export default {
  props: {
    center: {
      type: Object,
      default: () => ({
        lat: 0,
        lng: 0,
      }),
    },
  },
  data() {
    return {
      map: null,
      marker: null,
      autocomplete: null,
      placeService: null,
      infowindow: null,
      searchContent: '',
      showSearchResult: false,
      autoCompleteList: [],
      addressInfo: {
        address: '',
        position: {},
      },
      nativeGeocodeData: {}
    };
  },
  watch: {
    addressInfo: {
      handler(val) {
        if (val) {
          this.$emit('getInfo', val);
        }
      },
      deep: true,
    },
    searchContent(val) {
      if (!val) {
        return;
      }
      this.updateAutoComplete(val);
    },
  },
  methods: {
    // 初始化地图
    async initMap() {
      await loadGoogleMap()
      const maps = window.google?.maps;
      if (!maps) return;
      const { center } = this;
      this.map = new maps.Map(this.$refs.map, {
        center,
        zoom: 15,
        fullscreenControl: false,
        mapTypeControl: false,
        zoomControl: false,
        streetViewControl: false,
      });
      pushLog({
        key: 'nearest_map_inited',
        type: 'googlemap',
        url: this.$route.fullPath,
        env: process.env.NODE_ENV
      });
      // 去除掉地图上的默认标记
      this.map.setOptions({
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });
      const markerIcon = {
        path:
          'M5.12062 5.72065C11.6814 -0.840153 22.3186 -0.840154 28.8794 5.72065C35.4402 12.2815 35.4402 22.9186 28.8794 29.4794L17 41.3588L5.12062 29.4794C-1.44019 22.9186 -1.44019 12.2815 5.12062 5.72065ZM17 22.4001C19.651 22.4001 21.8 20.251 21.8 17.6C21.8 14.9491 19.651 12.8 17 12.8C14.349 12.8 12.2 14.9491 12.2 17.6C12.2 20.251 14.349 22.4001 17 22.4001Z',
        fillColor: '$primary',
        fillOpacity: 1,
        strokeColor: '$primary',
        strokeWeight: 0,
        rotation: 0,
        scale: 0.5,
        anchor: new maps.Point(17, 45),
      };
      this.marker = new maps.Marker({
        map: this.map,
        draggable: true,
        anchorPoint: new maps.Point(0, -13),
        icon: markerIcon,
      });
      this.marker.setPosition(center);
      this.marker.setVisible(true);
      this.initAutocomplete(maps, center);
      this.placeService = new window.google.maps.places.PlacesService(this.map);
    },
    async getPlaceDetail(placeId) {
      return new Promise((resolved, rejected) => {
        try {
          this.placeService.getDetails({ placeId }, (place, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place &&
              place.geometry &&
              place.geometry.location
            ) {
              resolved(place);
            } else {
              resolved(null);
            }
          });
        } catch (e) {
          resolved(null)
        }
      });
    },
    updateAutoComplete: debounce(function (searchContent) {
      const { center } = this;
      const defaultBounds = {
        north: center.lat + 0.1,
        south: center.lat - 0.1,
        east: center.lng + 0.1,
        west: center.lng - 0.1,
      };
      const options = {
        input: searchContent.trim(),
        bounds: defaultBounds,
        componentRestrictions: { country: ['id', 'cn'] },
        fields: ['address_components', 'geometry', 'icon', 'name'],
        strictBounds: false,
        // types: ['establishment'],
      };

      this.autocomplete.getPlacePredictions(options).then((data) => {
        console.log('data.predictions', data.predictions)
        if (Array.isArray(data.predictions)) {
          this.autoCompleteList = [...data.predictions];
          this.showSearchResult = true;
        } else {
          this.autoCompleteList = [];
          this.showSearchResult = true;
        }
      }).catch(e => {
        this.autoCompleteList = [];
        this.showSearchResult = false;
      });
    }, 1000),
    async onClickAddressItem($placeId) {
      this.infowindow.close();
      this.marker.setVisible(false);
      this.searchContent = '';
      this.showSearchResult = false;
      this.autoCompleteList = []
      if (!$placeId) return false;
      const place = await this.getPlaceDetail($placeId);
      if (!place) return false;
      console.log('place11', place);
      if (!place.geometry || !place.geometry.location) {
        // console.log(`No details available for input: '${place.name}'`);
        return;
      }
      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport);
      } else {
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(18);
      }
      this.addressInfo.position = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      this.marker.setPosition(place.geometry.location);
      this.marker.setVisible(true);

      const addressArr = place.address_components.map(
        item => item.short_name,
      );
      this.addressInfo.address = addressArr.join(' ');
      const infowindowContent = this.$refs.infowindow_content;
      infowindowContent.children[
        'place-name'
        ].textContent = this.addressInfo.address;
      this.infowindow.open(this.map, this.marker);
    },
    highLightStr(lightStr, targetStr) {
      if (!lightStr || !targetStr) {
        return '';
      }
      targetStr = String(targetStr)
      const reg = new RegExp(lightStr, 'gi');
      console.log('lightStr, targetStr', lightStr, targetStr)
      const resString = targetStr.replace(reg, (txt) => `<span style='color:#000;font-size: 18px;'>${txt}</span>`)
      return resString;
    },
    // 自动完成控件
    initAutocomplete(maps, center) {
      const inputBox = this.$refs.introInput;
      // 将控件置入地图
      this.map.controls[maps.ControlPosition.TOP_CENTER].push(inputBox);
      this.autocomplete = new window.google.maps.places.AutocompleteService();

      this.infowindow = new maps.InfoWindow();
      const infowindowContent = this.$refs.infowindow_content;

      this.infowindow.setContent(infowindowContent);

      const geocoder = new maps.Geocoder();

      geocoder.geocode({ location: center }, (results, status) => {
        if (results.length > 0) {
          this.addressInfo.position = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };
          this.addressInfo.address = results[0].formatted_address;
          infowindowContent.children[
            'place-name'
          ].textContent = this.addressInfo.address;
          this.infowindow.open(this.map, this.marker);

          [this.nativeGeocodeData] = results;
        }
      });

      this.marker.addListener('dragend', debounce(event => {
        this.map.setCenter(event.latLng);
        geocoder.geocode({ location: event.latLng }, (results, status) => {
          if (results.length > 0) {
            this.addressInfo.position = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            };
            this.addressInfo.address = results[0].formatted_address;
            infowindowContent.children[
              'place-name'
              ].textContent = this.addressInfo.address;
            this.infowindow.open(this.map, this.marker);

            [this.nativeGeocodeData] = results;
          }
        });
      }, 300));

//      autocomplete.addListener('place_changed', () => {
//        this.infowindow.close();
//        this.marker.setVisible(false);
//        const place = autocomplete.getPlace();
//        if (!place.geometry || !place.geometry.location) {
//          // console.log(`No details available for input: '${place.name}'`);
//          return;
//        }
//        if (place.geometry.viewport) {
//          this.map.fitBounds(place.geometry.viewport);
//        } else {
//          this.map.setCenter(place.geometry.location);
//          this.map.setZoom(18);
//        }
//        this.addressInfo.position = {
//          lat: place.geometry.location.lat(),
//          lng: place.geometry.location.lng(),
//        };
//        this.marker.setPosition(place.geometry.location);
//        this.marker.setVisible(true);
//
//        const addressArr = place.address_components.map(
//          item => item.short_name,
//        );
//        this.addressInfo.address = addressArr.join(' ');
//        infowindowContent.children[
//          'place-name'
//        ].textContent = this.addressInfo.address;
//        this.infowindow.open(this.map, this.marker);
//      });
    },
    // 地图标记
    handleSetMarker() {
      const maps = window.google?.maps;
      if (!maps) return;
      const infowindow = new maps.InfoWindow();
      const infowindowContent = this.$refs.infowindow_content;
    },
  },
};
</script>

<style lang="scss">
.nearest-map-box {
  height: 252px;
  #map {
    border: 1px solid #d1d5db;
    border-radius: 8px;
  }
  .introInput {
    top: 12px !important;
    width: 90%;
    .q-input {
      .q-field__control {
        background: #ffffff;
        width: 100%;
        height: 40px;
        border-radius: 8px;
        .q-field__prepend {
          height: 42px;
        }
        input {
          line-height: 41px;
        }
      }
    }
  }

  #infowindow-content {
    display: none;
    width: 100%;
    .title {
      font-weight: bold;
      color: $black;
    }
  }
  #introInput {
    display: none;
  }

  #map {
    #infowindow-content {
      padding: 12px !important;
      font-size: 12px;
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    #introInput {
      display: inline-block;
    }
    button.gm-ui-hover-effect {
      display: none !important;
    }
    .gm-style-iw {
      padding: 0 !important;
    }
    .gm-style-iw-d {
      font-size: 0;
      padding: 0 !important;
      overflow: unset !important;
    }
  }
}
</style>
<style lang='scss' scoped>
.search-result-panel {
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #dddddd;
  max-width: 100%;
  min-height: 40px;
  margin-top: 4px;
  padding: 12px 0;

  .result-item-desc {
    height: 24px;
    line-height: 24px;
    padding: 0 12px;
    color: #545454;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    margin-top: 0;
    margin-bottom: 8px;
    &:active {
      color: #ffdd00;
    }
  }
  .empty {
    height: 24px;
    line-height: 24px;
    color: #545454;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    padding-left: 12px;
  }
  .result-item {
    &:last-child {
      .result-item-desc {
        margin-bottom: 0;
      }
    }
  }
}
</style>
